import React, { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Trash2, Save, Printer, X, Loader2, Search, Palette, Layers, Package, ClipboardList, Sparkles, Pencil, Copy, Users, Link } from "lucide-react";
import { docsApi, api } from "../../api";

const KIND = "proposal";

// Fully blank content — used when the user hits "Blank Proposal".
const BLANK = () => ({
  title: "",
  prepared_for: "",
  client_company: "",
  proposal_date: new Date().toISOString().slice(0, 10),
  cover_image: "",
  brand: { primary: "#0f2c5c", accent: "#f59e0b", text: "#1f2937" },
  currency: "INR",
  overview: "",
  pages: [],
  design_visual: [],
  design_colors: [],
  timeline_note: "",
  packages: [],
  next_steps: [],
  signature: "",
  prepared_by: "",
});

const DEFAULTS = () => ({
  title: "Website Design & Development Proposal",
  prepared_for: "Kim",
  client_company: "KTH Customs Pty Ltd",
  proposal_date: new Date().toISOString().slice(0, 10),
  cover_image: "",
  brand: { primary: "#0f2c5c", accent: "#f59e0b", text: "#1f2937" },
  currency: "INR",
  overview:
    "Thank you for providing the draft website content.\n\n" +
    "Based on the supplied content and business requirements, we propose the design and development of a modern, responsive 3–5 page corporate website that showcases your services.\n\n" +
    "The new website will present a professional image, improve customer engagement, and make it easy for prospective clients to request quotations and contact your team.",
  pages: [
    { title: "Page 1 — Home", bullets: ["Professional hero banner", "Company introduction", "Key service highlights", "Call-to-action buttons (Request a Quote, Contact Us)"] },
    { title: "Page 2 — Services", bullets: ["Service list", "Feature highlights", "Testimonials"] },
    { title: "Page 3 — About Us", bullets: ["Company background", "Why choose us", "Team & credentials"] },
    { title: "Page 4 — Contact Us", bullets: ["Contact form", "Phone / Email / Address", "Google Maps integration"] },
  ],
  design_visual: [
    "Professional industry-relevant appearance",
    "Modern responsive layout",
    "Clean navigation structure",
    "Fast-loading pages",
    "Mobile, tablet and desktop compatible",
  ],
  design_colors: ["Red", "Dark Grey", "White"],
  timeline_note: "Estimated Completion: 1 week",
  packages: [
    {
      name: "PACKAGE 1 — STATIC BUSINESS WEBSITE",
      items: [
        { description: "Up to 5 Pages", cost: "Included" },
        { description: "Custom Design", cost: "Included" },
        { description: "Mobile Responsive", cost: "Included" },
        { description: "Contact Form", cost: "Included" },
        { description: "Google Maps", cost: "Included" },
        { description: "Basic SEO Setup", cost: "Included" },
        { description: "1 Round of Revisions", cost: "Included" },
      ],
      total: 750,
    },
    {
      name: "PACKAGE 2 — DYNAMIC WORDPRESS WEBSITE",
      items: [
        { description: "WordPress CMS", cost: "Included" },
        { description: "Custom Design", cost: "Included" },
        { description: "Up to 5 Pages", cost: "Included" },
        { description: "Contact Form", cost: "Included" },
        { description: "Google Maps", cost: "Included" },
        { description: "Basic SEO Setup", cost: "Included" },
        { description: "Easy Content Management", cost: "Included" },
        { description: "Admin Training", cost: "Included" },
        { description: "2 Rounds of Revisions", cost: "Included" },
      ],
      total: 1250,
    },
  ],
  next_steps: [
    "Approval of proposal",
    "50% deposit upfront, remaining 50% on completion",
    "Begin development, testing and final review",
  ],
  signature: "Jot Singh",
  prepared_by: "Navjot.com Australia",
});

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$", JPY: "¥" };

/* ─────────────────────────────────────────────────────────────
   30 design templates — each is a small preset that primes the
   new proposal's title, industry copy and brand palette. When a
   user picks one, we spread it over BLANK() so only the fields
   the template chose to fill are set.
   ───────────────────────────────────────────────────────────── */
const TEMPLATES = [
  // 1-10 · Services & Digital
  { key: "blank",     name: "Blank Canvas",        industry: "Any",           brand: { primary: "#0f172a", accent: "#6366f1", text: "#1f2937" }, title: "" },
  { key: "web",       name: "Website Design",      industry: "Web / Digital", brand: { primary: "#0f2c5c", accent: "#f59e0b", text: "#1f2937" }, title: "Website Design & Development Proposal" },
  { key: "mobile",    name: "Mobile App Build",    industry: "Mobile",        brand: { primary: "#111827", accent: "#22d3ee", text: "#1f2937" }, title: "Mobile App Development Proposal" },
  { key: "saas",      name: "SaaS Platform",       industry: "SaaS",          brand: { primary: "#4c1d95", accent: "#a78bfa", text: "#1f2937" }, title: "SaaS Platform Delivery Proposal" },
  { key: "ecom",      name: "E-commerce Store",    industry: "Retail",        brand: { primary: "#0f766e", accent: "#fb923c", text: "#1f2937" }, title: "E-commerce Store Build Proposal" },
  { key: "brand",     name: "Brand Identity",      industry: "Branding",      brand: { primary: "#1e1b4b", accent: "#f43f5e", text: "#1f2937" }, title: "Brand Identity Design Proposal" },
  { key: "seo",       name: "SEO & Growth",        industry: "Marketing",     brand: { primary: "#064e3b", accent: "#10b981", text: "#1f2937" }, title: "SEO & Growth Proposal" },
  { key: "content",   name: "Content Marketing",   industry: "Marketing",     brand: { primary: "#7c2d12", accent: "#fbbf24", text: "#1f2937" }, title: "Content Marketing Proposal" },
  { key: "social",    name: "Social Media",        industry: "Marketing",     brand: { primary: "#831843", accent: "#f472b6", text: "#1f2937" }, title: "Social Media Retainer Proposal" },
  { key: "ads",       name: "Paid Ads Campaign",   industry: "Marketing",     brand: { primary: "#1e3a8a", accent: "#38bdf8", text: "#1f2937" }, title: "Paid Ads Campaign Proposal" },

  // 11-20 · Services
  { key: "consult",   name: "Consulting Engagement", industry: "Consulting", brand: { primary: "#0c4a6e", accent: "#94a3b8", text: "#1f2937" }, title: "Consulting Engagement Proposal" },
  { key: "training",  name: "Corporate Training",  industry: "Training",      brand: { primary: "#155e75", accent: "#f97316", text: "#1f2937" }, title: "Corporate Training Programme Proposal" },
  { key: "video",     name: "Video Production",    industry: "Media",         brand: { primary: "#18181b", accent: "#ef4444", text: "#1f2937" }, title: "Video Production Proposal" },
  { key: "photo",     name: "Photography",         industry: "Media",         brand: { primary: "#292524", accent: "#eab308", text: "#1f2937" }, title: "Photography Package Proposal" },
  { key: "wedding",   name: "Wedding Photography", industry: "Events",        brand: { primary: "#701a75", accent: "#fbcfe8", text: "#1f2937" }, title: "Wedding Photography Proposal" },
  { key: "event",     name: "Event Management",    industry: "Events",        brand: { primary: "#7f1d1d", accent: "#fde047", text: "#1f2937" }, title: "Event Management Proposal" },
  { key: "restaurant",name: "Restaurant Concept",  industry: "F&B",           brand: { primary: "#78350f", accent: "#dc2626", text: "#1f2937" }, title: "Restaurant Concept & Launch Proposal" },
  { key: "cafe",      name: "Café Rebrand",        industry: "F&B",           brand: { primary: "#3f6212", accent: "#facc15", text: "#1f2937" }, title: "Café Rebrand Proposal" },
  { key: "interior",  name: "Interior Design",     industry: "Architecture",  brand: { primary: "#1c1917", accent: "#d4a373", text: "#1f2937" }, title: "Interior Design Proposal" },
  { key: "arch",      name: "Architecture",        industry: "Architecture",  brand: { primary: "#0f172a", accent: "#f4f4f5", text: "#1f2937" }, title: "Architecture Concept Proposal" },

  // 21-30 · Enterprise / Specialty
  { key: "b2b",       name: "B2B Sales Deck",      industry: "Sales",         brand: { primary: "#082f49", accent: "#38bdf8", text: "#1f2937" }, title: "B2B Partnership Proposal" },
  { key: "rfp",       name: "Enterprise RFP",      industry: "Enterprise",    brand: { primary: "#111827", accent: "#a3e635", text: "#1f2937" }, title: "Enterprise RFP Response" },
  { key: "gov",       name: "Government Tender",   industry: "Public sector", brand: { primary: "#052e16", accent: "#fbbf24", text: "#1f2937" }, title: "Government Tender Response" },
  { key: "nonprofit", name: "Non-profit Grant",    industry: "Non-profit",    brand: { primary: "#134e4a", accent: "#f97316", text: "#1f2937" }, title: "Non-profit Grant Proposal" },
  { key: "edu",       name: "Education Programme", industry: "Education",     brand: { primary: "#312e81", accent: "#f59e0b", text: "#1f2937" }, title: "Education Programme Proposal" },
  { key: "health",    name: "Healthcare Services", industry: "Healthcare",    brand: { primary: "#134e4a", accent: "#22d3ee", text: "#1f2937" }, title: "Healthcare Services Proposal" },
  { key: "legal",     name: "Legal Retainer",      industry: "Legal",         brand: { primary: "#1e1b4b", accent: "#c4b5fd", text: "#1f2937" }, title: "Legal Retainer Proposal" },
  { key: "finance",   name: "Financial Advisory",  industry: "Finance",       brand: { primary: "#052e16", accent: "#22c55e", text: "#1f2937" }, title: "Financial Advisory Proposal" },
  { key: "real",      name: "Real Estate Deal",    industry: "Real Estate",   brand: { primary: "#365314", accent: "#fbbf24", text: "#1f2937" }, title: "Real Estate Investment Proposal" },
  { key: "startup",   name: "Startup Pitch",       industry: "Startup",       brand: { primary: "#0f172a", accent: "#ec4899", text: "#1f2937" }, title: "Startup Pitch Proposal" },
];

// Colorful preview swatch card style used inside the gallery.
function templatePreview(t) {
  return {
    background: `linear-gradient(135deg, ${t.brand.primary} 0%, ${t.brand.primary} 55%, ${t.brand.accent} 100%)`,
  };
}

export default function Proposal({ fromLead }) {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [showGallery, setShowGallery] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await docsApi.list(KIND);
      const sorted = [...list].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      setDocs(sorted);
    } catch { setDocs([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // If opened from CRM, auto-create a new proposal prefilled with lead data
  useEffect(() => {
    if (!fromLead) return;
    const base = BLANK();
    base.prepared_for = fromLead.name || "";
    base.client_company = fromLead.company || fromLead.name || "";
    base.title = `Proposal for ${fromLead.name || fromLead.company || "Client"}`;
    base.currency = "INR";
    if (fromLead.deal_amount) {
      base.packages = [{
        name: "PROPOSED PACKAGE",
        items: [{ description: "As discussed", cost: String(fromLead.deal_amount) }],
      }];
    }
    setActive({
      id: null,
      title: base.title,
      content: base,
      _fromLead: fromLead,       // keep the source lead reference
      crm_lead_id: fromLead.id,
    });
    setShowGallery(false);
  }, [fromLead]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return docs;
    return docs.filter(d => (d.title || "").toLowerCase().includes(s));
  }, [docs, q]);

  const startNew = () => {
    // Show template gallery — user picks BLANK or one of 30 designs.
    setShowGallery(true);
  };

  const createFromTemplate = (t) => {
    const base = BLANK();
    if (t && t.key !== "blank") {
      base.title = t.title || base.title;
      base.brand = { ...base.brand, ...t.brand };
    }
    setActive({ id: null, title: base.title || "Untitled Proposal", content: base });
    setShowGallery(false);
  };

  const cloneDoc = async (d) => {
    try {
      const parsed = typeof d.content === "string"
        ? (() => { try { return JSON.parse(d.content); } catch { return BLANK(); } })()
        : d.content || BLANK();
      const created = await docsApi.create({
        kind: KIND,
        title: `Copy of ${d.title || "Untitled Proposal"}`,
        content: { ...parsed, title: `Copy of ${parsed.title || d.title || ""}` },
      });
      setDocs(list => [created, ...list]);
    } catch (e) { console.error(e); }
  };

  const openDoc = (d) => {
    const parsed = typeof d.content === "string"
      ? (() => { try { return JSON.parse(d.content); } catch { return BLANK(); } })()
      : d.content || BLANK();
    setActive({ ...d, content: { ...BLANK(), ...parsed } });
  };

  const saveDoc = async () => {
    if (!active) return;
    setSaving(true);
    try {
      if (active.id) {
        const updated = await docsApi.update(active.id, {
          kind: KIND,
          title: active.content.title || "Untitled Proposal",
          content: active.content,
          crm_lead_id: active.crm_lead_id || undefined,
        });
        setDocs(list => list.map(d => d.id === updated.id ? updated : d));
        setActive({ ...active, ...updated });
      } else {
        const created = await docsApi.create({
          kind: KIND,
          title: active.content.title || "Untitled Proposal",
          content: active.content,
          crm_lead_id: active.crm_lead_id || undefined,
        });
        setDocs(list => [created, ...list]);
        setActive({ ...active, ...created });
      }
    } catch (e) {
      alert("Save failed: " + (e?.message || "error"));
    }
    setSaving(false);
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    await docsApi.remove(id);
    setDocs(list => list.filter(d => d.id !== id));
    if (active?.id === id) setActive(null);
  };

  const patch = (partial) => setActive(a => ({ ...a, content: { ...a.content, ...partial } }));

  const printProposal = () => {
    if (!active) return;
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    w.document.write(renderProposalHTML(active.content));
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (_) { /* noop */ } }, 500);
  };

  if (!active) {
    return (
      <div className="h-full w-full flex flex-col bg-slate-50 text-slate-800 hexa-force-light" data-testid="app-proposal">
      <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
          <FileText className="w-5 h-5 text-indigo-500" />
          <div className="text-sm font-bold">Proposal Builder</div>
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search proposals…" className="pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded outline-none w-56" data-testid="proposal-search" />
          </div>
          <button onClick={startNew} data-testid="proposal-new-btn" className="text-xs font-semibold px-3 py-1.5 rounded bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:opacity-90 flex items-center gap-1">
            <Plus size={12} /> New Proposal
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-500 py-16">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <div className="text-sm font-semibold">No proposals yet</div>
              <div className="text-xs mt-1">Create your first proposal — cover page, scope, packages, terms and export as PDF.</div>
              <button onClick={startNew} className="mt-4 text-xs font-semibold px-3 py-1.5 rounded bg-slate-900 text-white">Create Proposal</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map(d => (
                <div key={d.id} className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition p-4 cursor-pointer" onClick={() => openDoc(d)} data-testid={`proposal-item-${d.id}`}>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 line-clamp-2">{d.title || "Untitled Proposal"}</div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {(d.content?.client_company) || "—"} · {(d.content?.proposal_date) || ""}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); openDoc(d); }}
                      title="Edit"
                      data-testid={`proposal-edit-${d.id}`}
                      className="text-slate-400 hover:text-indigo-600 p-1"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); cloneDoc(d); }}
                      title="Clone / Copy"
                      data-testid={`proposal-clone-${d.id}`}
                      className="text-slate-400 hover:text-emerald-600 p-1"
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeDoc(d.id); }}
                      title="Delete"
                      data-testid={`proposal-delete-${d.id}`}
                      className="text-slate-400 hover:text-red-500 p-1"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showGallery && (
          <div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowGallery(false)}>
            <div
              className="hexa-force-light bg-white rounded-xl shadow-2xl w-[880px] max-w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="proposal-template-gallery"
            >
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-800">Pick a starting design</div>
                  <div className="text-xs text-slate-500">30 industry-tuned templates · plus a blank canvas</div>
                </div>
                <button onClick={() => setShowGallery(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
              </div>
              <div className="p-5 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {TEMPLATES.map(t => (
                  <button
                    key={t.key}
                    onClick={() => createFromTemplate(t)}
                    data-testid={`proposal-tpl-${t.key}`}
                    className="group text-left rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-400 hover:shadow-md transition bg-white"
                  >
                    <div className="h-24 relative" style={templatePreview(t)}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FileText className="text-white/80" size={26} strokeWidth={1.5} />
                      </div>
                      {t.key === "blank" && (
                        <span className="absolute top-1 right-1 text-[9px] px-1.5 py-0.5 rounded bg-white/90 text-slate-700 font-bold uppercase tracking-wider">Blank</span>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-bold text-slate-800 truncate">{t.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{t.industry}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const c = active.content;

  return (
    <div className="h-full w-full flex flex-col bg-slate-50 text-slate-800 hexa-force-light" data-testid="app-proposal">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-white flex items-center gap-2">
        <button onClick={() => setActive(null)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><X size={14} /></button>
        <FileText className="w-4 h-4 text-indigo-500" />
        <input value={c.title} onChange={(e) => patch({ title: e.target.value })} className="text-sm font-semibold flex-1 max-w-xl px-2 py-1 border border-transparent hover:border-slate-200 focus:border-indigo-400 rounded outline-none" data-testid="proposal-title-input" />
        <div className="flex-1" />
        <button onClick={saveDoc} disabled={saving} className="text-xs font-semibold px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1 disabled:opacity-60" data-testid="proposal-save-btn">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
        <button onClick={printProposal} className="text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 flex items-center gap-1" data-testid="proposal-print-btn">
          <Printer size={12} /> Print / PDF
        </button>
      </div>

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* LEFT: Editor */}
        <div className="w-96 shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
          <Section title="Cover Page" icon={Sparkles}>
            <Field label="Prepared for">
              <input value={c.prepared_for} onChange={(e) => patch({ prepared_for: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" data-testid="proposal-prepared-for" />
            </Field>
            <Field label="Client company">
              <input value={c.client_company} onChange={(e) => patch({ client_company: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" data-testid="proposal-client-company" />
            </Field>
            <Field label="Date">
              <input type="date" value={c.proposal_date} onChange={(e) => patch({ proposal_date: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" />
            </Field>
            <Field label="Cover image (optional)">
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
                  patch({ cover_image: b64 });
                }} />
                <span className="px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50">Upload image</span>
                {c.cover_image && <span className="text-[10px] text-slate-500 truncate">Image set ✓</span>}
              </label>
            </Field>
          </Section>

          <Section title="Brand" icon={Palette}>
            <div className="grid grid-cols-3 gap-2">
              <ColorField label="Primary" value={c.brand.primary} onChange={(v) => patch({ brand: { ...c.brand, primary: v } })} />
              <ColorField label="Accent"  value={c.brand.accent}  onChange={(v) => patch({ brand: { ...c.brand, accent: v } })} />
              <ColorField label="Text"    value={c.brand.text}    onChange={(v) => patch({ brand: { ...c.brand, text: v } })} />
            </div>
          </Section>

          <Section title="Project Overview" icon={ClipboardList}>
            <textarea rows={6} value={c.overview} onChange={(e) => patch({ overview: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none resize-y" data-testid="proposal-overview" />
          </Section>

          <Section title="Scope — Pages" icon={Layers}>
            {c.pages.map((p, idx) => (
              <div key={idx} className="p-2 mb-2 rounded border border-slate-200 space-y-1.5">
                <div className="flex items-center gap-1">
                  <input value={p.title} onChange={(e) => patch({ pages: c.pages.map((x, i) => i === idx ? { ...x, title: e.target.value } : x) })} className="flex-1 text-sm font-semibold px-2 py-1 rounded border border-slate-100 focus:border-indigo-400 outline-none" />
                  <button onClick={() => patch({ pages: c.pages.filter((_, i) => i !== idx) })} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                </div>
                <ArrayEditor items={p.bullets} onChange={(v) => patch({ pages: c.pages.map((x, i) => i === idx ? { ...x, bullets: v } : x) })} placeholder="Bullet point" />
              </div>
            ))}
            <button onClick={() => patch({ pages: [...c.pages, { title: `Page ${c.pages.length + 1}`, bullets: [""] }] })} className="text-xs text-indigo-600 font-semibold flex items-center gap-1 hover:text-indigo-700"><Plus size={11} /> Add page</button>
          </Section>

          <Section title="Design Concept" icon={Palette}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Visual style</div>
            <ArrayEditor items={c.design_visual} onChange={(v) => patch({ design_visual: v })} placeholder="Attribute" />
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-3 mb-1">Colour scheme</div>
            <ArrayEditor items={c.design_colors} onChange={(v) => patch({ design_colors: v })} placeholder="Colour" />
          </Section>

          <Section title="Timeline" icon={ClipboardList}>
            <input value={c.timeline_note} onChange={(e) => patch({ timeline_note: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" data-testid="proposal-timeline" />
          </Section>

          <Section title="Packages" icon={Package}>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Currency</span>
              <select value={c.currency} onChange={(e) => patch({ currency: e.target.value })} className="text-xs px-2 py-1 border border-slate-200 rounded outline-none bg-white">
                {Object.keys(CURRENCY_SYMBOL).map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            {c.packages.map((pkg, pi) => (
              <div key={pi} className="p-2 mb-2 rounded border border-slate-200 space-y-1.5">
                <div className="flex items-center gap-1">
                  <input value={pkg.name} onChange={(e) => patch({ packages: c.packages.map((x, i) => i === pi ? { ...x, name: e.target.value } : x) })} className="flex-1 text-sm font-semibold px-2 py-1 rounded border border-slate-100 focus:border-indigo-400 outline-none" />
                  <button onClick={() => patch({ packages: c.packages.filter((_, i) => i !== pi) })} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                </div>
                {pkg.items.map((it, ii) => (
                  <div key={ii} className="grid grid-cols-8 gap-1">
                    <input value={it.description} onChange={(e) => patch({ packages: c.packages.map((x, i) => i === pi ? { ...x, items: x.items.map((y, j) => j === ii ? { ...y, description: e.target.value } : y) } : x) })} placeholder="Description" className="col-span-5 text-xs px-2 py-1 rounded border border-slate-200 outline-none" />
                    <input value={it.cost} onChange={(e) => patch({ packages: c.packages.map((x, i) => i === pi ? { ...x, items: x.items.map((y, j) => j === ii ? { ...y, cost: e.target.value } : y) } : x) })} placeholder="Cost" className="col-span-2 text-xs px-2 py-1 rounded border border-slate-200 outline-none" />
                    <button onClick={() => patch({ packages: c.packages.map((x, i) => i === pi ? { ...x, items: x.items.filter((_, j) => j !== ii) } : x) })} className="text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <button onClick={() => patch({ packages: c.packages.map((x, i) => i === pi ? { ...x, items: [...x.items, { description: "", cost: "Included" }] } : x) })} className="text-[11px] text-indigo-600 flex items-center gap-1 hover:text-indigo-700"><Plus size={10} /> Add line</button>
                  <div className="flex-1" />
                  <span className="text-[10px] text-slate-500">Total</span>
                  <input type="number" value={pkg.total} onChange={(e) => patch({ packages: c.packages.map((x, i) => i === pi ? { ...x, total: Number(e.target.value) } : x) })} className="w-24 text-xs px-2 py-1 rounded border border-slate-200 outline-none font-semibold" />
                </div>
              </div>
            ))}
            <button onClick={() => patch({ packages: [...c.packages, { name: `PACKAGE ${c.packages.length + 1}`, items: [{ description: "", cost: "Included" }], total: 0 }] })} className="text-xs text-indigo-600 font-semibold flex items-center gap-1 hover:text-indigo-700"><Plus size={11} /> Add package</button>
          </Section>

          <Section title="Next Steps" icon={ClipboardList}>
            <ArrayEditor items={c.next_steps} onChange={(v) => patch({ next_steps: v })} placeholder="Step" />
            <Field label="Signature">
              <input value={c.signature} onChange={(e) => patch({ signature: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" />
            </Field>
            <Field label="Prepared by">
              <input value={c.prepared_by} onChange={(e) => patch({ prepared_by: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none" />
            </Field>
          </Section>
        </div>

        {/* RIGHT: Live preview */}
        <div className="flex-1 min-w-0 overflow-y-auto bg-slate-200 p-6" data-testid="proposal-preview">
          <div className="mx-auto shadow-2xl" style={{ maxWidth: 820 }}>
            <ProposalPreview c={c} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="p-3 border-b border-slate-100">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1.5">
        {Icon && <Icon size={11} className="text-indigo-500" />} {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-1">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-8 h-7 rounded border border-slate-200 cursor-pointer" />
        <input value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 text-[11px] font-mono px-1.5 py-1 rounded border border-slate-200 outline-none" />
      </div>
    </div>
  );
}

function ArrayEditor({ items, onChange, placeholder }) {
  const list = items || [];
  return (
    <div className="space-y-1.5">
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-1">
          <input value={v} onChange={(e) => onChange(list.map((x, j) => j === i ? e.target.value : x))} placeholder={placeholder} className="flex-1 text-xs px-2 py-1 rounded border border-slate-200 outline-none focus:border-indigo-400" />
          <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...list, ""])} className="text-[11px] text-indigo-600 flex items-center gap-1 hover:text-indigo-700"><Plus size={10} /> Add item</button>
    </div>
  );
}

/* ============================================================
   Live A4 preview — matches the printable output.
   ============================================================ */

function ProposalPreview({ c }) {
  const symbol = CURRENCY_SYMBOL[c.currency] || "₹";
  return (
    <div className="bg-white text-slate-900" style={{ minHeight: 1160 }}>
      <PageWave color={c.brand.primary} accent={c.brand.accent} />
      {/* Cover */}
      <div className="px-12 py-14 relative" data-testid="proposal-preview-cover">
        {c.cover_image && (
          <div className="w-56 h-56 rounded-full overflow-hidden mx-auto mb-8 border-4" style={{ borderColor: c.brand.accent }}>
            <img src={c.cover_image} alt="cover" className="w-full h-full object-cover" />
          </div>
        )}
        <h1 className="text-3xl font-extrabold leading-tight uppercase mb-4" style={{ color: c.brand.primary }}>
          {c.title} {c.client_company ? <>for <span style={{ color: c.brand.primary }}>{c.client_company}</span></> : null}
        </h1>
        <div className="text-sm text-slate-600">Prepared for: <span className="font-semibold" style={{ color: c.brand.text }}>{c.prepared_for}</span></div>
        <div className="text-sm text-slate-500 mt-1">{formatDate(c.proposal_date)}</div>
      </div>
      <PageFoot color={c.brand.primary} accent={c.brand.accent} />

      {/* Overview */}
      <PageBreak />
      <PageWave color={c.brand.primary} accent={c.brand.accent} />
      <div className="px-12 py-10">
        <SectionHead label="PROJECT OVERVIEW" primary={c.brand.primary} />
        <div className="mt-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: c.brand.text }}>{c.overview}</div>
      </div>
      <PageFoot color={c.brand.primary} accent={c.brand.accent} />

      {/* Scope */}
      <PageBreak />
      <PageWave color={c.brand.primary} accent={c.brand.accent} />
      <div className="px-12 py-10">
        <SectionHead label="INCLUSIONS & SCOPE OF WORK" primary={c.brand.primary} />
        <div className="mt-2 text-lg font-bold" style={{ color: c.brand.primary }}>Proposed Website Structure</div>
        <div className="mt-4 space-y-5">
          {c.pages.map((p, i) => (
            <div key={i}>
              <div className="text-sm font-bold" style={{ color: c.brand.primary }}>{p.title}</div>
              <ul className="mt-1 ml-5 list-disc text-sm space-y-1" style={{ color: c.brand.text }}>
                {(p.bullets || []).filter(Boolean).map((b, j) => <li key={j}>{b}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <PageFoot color={c.brand.primary} accent={c.brand.accent} />

      {/* Design Concept */}
      <PageBreak />
      <PageWave color={c.brand.primary} accent={c.brand.accent} />
      <div className="px-12 py-10">
        <SectionHead label="DESIGN CONCEPT" primary={c.brand.primary} />
        <div className="mt-4 text-base font-bold" style={{ color: c.brand.primary }}>Visual Style</div>
        <ul className="mt-1 ml-5 list-disc text-sm space-y-1" style={{ color: c.brand.text }}>
          {c.design_visual.filter(Boolean).map((v, i) => <li key={i}>{v}</li>)}
        </ul>
        <div className="mt-6 text-base font-bold" style={{ color: c.brand.primary }}>Colour Scheme</div>
        <ul className="mt-1 ml-5 list-disc text-sm space-y-1" style={{ color: c.brand.text }}>
          {c.design_colors.filter(Boolean).map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      </div>
      <PageFoot color={c.brand.primary} accent={c.brand.accent} />

      {/* Packages */}
      {c.packages.map((pkg, pi) => (
        <React.Fragment key={pi}>
          <PageBreak />
          <PageWave color={c.brand.primary} accent={c.brand.accent} />
          <div className="px-12 py-10">
            <SectionHead label="TIMELINE" primary={c.brand.primary} />
            <div className="mt-2 text-sm font-semibold" style={{ color: c.brand.text }}>{c.timeline_note}</div>
            <div className="mt-6 text-lg font-extrabold" style={{ color: c.brand.primary }}>{pkg.name}</div>
            <table className="mt-3 w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-2 border-b-2" style={{ borderColor: c.brand.primary, color: c.brand.primary }}>Description</th>
                  <th className="text-right py-2 border-b-2 w-40" style={{ borderColor: c.brand.primary, color: c.brand.primary }}>Cost ({c.currency})</th>
                </tr>
              </thead>
              <tbody>
                {pkg.items.map((it, i) => (
                  <tr key={i}>
                    <td className="py-1.5 border-b border-slate-200" style={{ color: c.brand.text }}>{it.description}</td>
                    <td className="py-1.5 text-right border-b border-slate-200" style={{ color: c.brand.text }}>{it.cost}</td>
                  </tr>
                ))}
                <tr>
                  <td className="pt-3 font-extrabold" style={{ color: c.brand.primary }}>Total</td>
                  <td className="pt-3 text-right font-extrabold" style={{ color: c.brand.accent }}>{symbol}{Number(pkg.total || 0).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <PageFoot color={c.brand.primary} accent={c.brand.accent} />
        </React.Fragment>
      ))}

      {/* Next Steps */}
      <PageBreak />
      <PageWave color={c.brand.primary} accent={c.brand.accent} />
      <div className="px-12 py-10">
        <SectionHead label="NEXT STEPS" primary={c.brand.primary} />
        <ul className="mt-4 ml-5 list-disc text-sm space-y-1" style={{ color: c.brand.text }}>
          {c.next_steps.filter(Boolean).map((v, i) => <li key={i}>{v}</li>)}
        </ul>
        <div className="mt-10 text-sm" style={{ color: c.brand.text }}>Thank you for considering this proposal.</div>
        <div className="mt-2 text-base font-bold" style={{ color: c.brand.primary }}>{c.signature}</div>
        <div className="mt-8 text-[11px] uppercase tracking-widest text-slate-500">Prepared by: <span className="font-semibold" style={{ color: c.brand.text }}>{c.prepared_by}</span></div>
      </div>
      <PageFoot color={c.brand.primary} accent={c.brand.accent} />
    </div>
  );
}

function PageBreak() { return <div className="h-6 bg-slate-100" />; }

function SectionHead({ label, primary }) {
  return (
    <div className="text-2xl font-extrabold uppercase tracking-wide" style={{ color: primary }}>{label}</div>
  );
}

function PageWave({ color, accent }) {
  return (
    <svg viewBox="0 0 800 80" preserveAspectRatio="none" className="block w-full" style={{ height: 60 }}>
      <rect x="0" y="0" width="800" height="80" fill={color} />
      <path d="M0,60 C 200,30 400,90 800,40 L 800,80 L 0,80 Z" fill={accent} opacity="0.9" />
    </svg>
  );
}
function PageFoot({ color, accent }) {
  return (
    <svg viewBox="0 0 800 60" preserveAspectRatio="none" className="block w-full" style={{ height: 44 }}>
      <path d="M0,20 C 200,-10 400,50 800,10 L 800,60 L 0,60 Z" fill={accent} opacity="0.9" />
      <rect x="0" y="30" width="800" height="30" fill={color} />
    </svg>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, "0");
    const mon = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${mon}/${d.getFullYear()}`;
  } catch { return iso; }
}

/* ============================================================
   Printable HTML — mirrors ProposalPreview but standalone.
   Uses inline styles so print retains colors.
   ============================================================ */
function renderProposalHTML(c) {
  const primary = c.brand?.primary || "#0f2c5c";
  const accent  = c.brand?.accent  || "#f59e0b";
  const text    = c.brand?.text    || "#1f2937";
  const symbol  = CURRENCY_SYMBOL[c.currency] || "₹";
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, m => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m]));

  const wave = `<svg viewBox="0 0 800 80" preserveAspectRatio="none" style="display:block;width:100%;height:60px"><rect width="800" height="80" fill="${primary}"/><path d="M0,60 C 200,30 400,90 800,40 L 800,80 L 0,80 Z" fill="${accent}" opacity="0.9"/></svg>`;
  const foot = `<svg viewBox="0 0 800 60" preserveAspectRatio="none" style="display:block;width:100%;height:44px"><path d="M0,20 C 200,-10 400,50 800,10 L 800,60 L 0,60 Z" fill="${accent}" opacity="0.9"/><rect y="30" width="800" height="30" fill="${primary}"/></svg>`;

  const packagesHtml = c.packages.map(pkg => `
    <div class="page">
      ${wave}
      <div class="content">
        <h2 style="color:${primary}">TIMELINE</h2>
        <div style="margin-top:6px;font-weight:600;color:${text}">${esc(c.timeline_note)}</div>
        <div style="margin-top:26px;font-weight:800;font-size:20px;color:${primary}">${esc(pkg.name)}</div>
        <table>
          <thead><tr>
            <th style="text-align:left;border-bottom:2px solid ${primary};padding:8px 0;color:${primary}">Description</th>
            <th style="text-align:right;border-bottom:2px solid ${primary};padding:8px 0;color:${primary};width:180px">Cost (${esc(c.currency)})</th>
          </tr></thead>
          <tbody>
            ${pkg.items.map(it => `<tr><td style="padding:6px 0;border-bottom:1px solid #e5e7eb;color:${text}">${esc(it.description)}</td><td style="padding:6px 0;border-bottom:1px solid #e5e7eb;color:${text};text-align:right">${esc(it.cost)}</td></tr>`).join("")}
            <tr><td style="padding-top:12px;font-weight:800;color:${primary}">Total</td><td style="padding-top:12px;text-align:right;font-weight:800;color:${accent}">${symbol}${Number(pkg.total||0).toLocaleString()}</td></tr>
          </tbody>
        </table>
      </div>
      ${foot}
    </div>`).join("");

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(c.title)}</title>
<style>
  @page { size: A4; margin: 0; }
  body { margin: 0; font-family: 'Inter', system-ui, sans-serif; color:${text}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { page-break-after: always; min-height: 100vh; display: flex; flex-direction: column; }
  .page .content { flex: 1; padding: 48px 56px; }
  .cover { text-align: left; }
  .cover-img { width: 220px; height: 220px; border-radius: 50%; overflow: hidden; margin: 0 auto 32px; border: 4px solid ${accent}; }
  .cover-img img { width: 100%; height: 100%; object-fit: cover; }
  h1 { font-size: 28px; text-transform: uppercase; margin: 0 0 12px; color: ${primary}; line-height: 1.2; }
  h2 { font-size: 24px; text-transform: uppercase; margin: 0; color: ${primary}; }
  ul { margin: 6px 0 0 22px; }
  li { margin: 3px 0; color: ${text}; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
  .meta { color: #64748b; font-size: 13px; margin-top: 4px; }
</style></head><body>

<div class="page">
  ${wave}
  <div class="content cover">
    ${c.cover_image ? `<div class="cover-img"><img src="${esc(c.cover_image)}"/></div>` : ""}
    <h1>${esc(c.title)}${c.client_company ? ` for ${esc(c.client_company)}` : ""}</h1>
    <div style="color:${text};font-weight:600;font-size:14px">Prepared for: ${esc(c.prepared_for)}</div>
    <div class="meta">${esc(formatDate(c.proposal_date))}</div>
  </div>
  ${foot}
</div>

<div class="page">
  ${wave}
  <div class="content">
    <h2>PROJECT OVERVIEW</h2>
    <div style="margin-top:16px;font-size:14px;line-height:1.6;white-space:pre-wrap">${esc(c.overview)}</div>
  </div>
  ${foot}
</div>

<div class="page">
  ${wave}
  <div class="content">
    <h2>INCLUSIONS &amp; SCOPE OF WORK</h2>
    <div style="margin-top:10px;font-weight:800;font-size:18px;color:${primary}">Proposed Website Structure</div>
    ${c.pages.map(p => `<div style="margin-top:14px"><div style="font-weight:700;font-size:14px;color:${primary}">${esc(p.title)}</div><ul>${(p.bullets||[]).filter(Boolean).map(b => `<li>${esc(b)}</li>`).join("")}</ul></div>`).join("")}
  </div>
  ${foot}
</div>

<div class="page">
  ${wave}
  <div class="content">
    <h2>DESIGN CONCEPT</h2>
    <div style="margin-top:16px;font-weight:700;font-size:15px;color:${primary}">Visual Style</div>
    <ul>${c.design_visual.filter(Boolean).map(v => `<li>${esc(v)}</li>`).join("")}</ul>
    <div style="margin-top:20px;font-weight:700;font-size:15px;color:${primary}">Colour Scheme</div>
    <ul>${c.design_colors.filter(Boolean).map(v => `<li>${esc(v)}</li>`).join("")}</ul>
  </div>
  ${foot}
</div>

${packagesHtml}

<div class="page">
  ${wave}
  <div class="content">
    <h2>NEXT STEPS</h2>
    <ul>${c.next_steps.filter(Boolean).map(v => `<li>${esc(v)}</li>`).join("")}</ul>
    <div style="margin-top:40px;font-size:14px">Thank you for considering this proposal.</div>
    <div style="margin-top:6px;font-weight:700;color:${primary}">${esc(c.signature)}</div>
    <div style="margin-top:32px;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:2px">Prepared by: <span style="color:${text};font-weight:600">${esc(c.prepared_by)}</span></div>
  </div>
  ${foot}
</div>

</body></html>`;
}
